import './sp-hierarchical-select-dialog';

import {css, html, nothing, PropertyValues} from 'lit';
import {customElement, property, queryAsync} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {TemplateResult} from 'lit-html';
import {DirectiveResult} from 'lit/directive';
import {SpDefaultDialog} from '../../sp-dialog/sp-default-dialog';
import {SpBaseInput} from "../sp-base-input";
import {unsafeCSSIcon} from "../../../styles/icons";
import {unsafeHTML} from 'lit/directives/unsafe-html.js';

@customElement('sp-hierarchical-select')
export class SpHierarchicalSelect extends SpBaseInput<(string | number) | (string | number)[]> {
    @property({type: Array})
    options: SpHierarchicalSelectOption[];
    @property()
    value;
    @property({type: Function})
    valueToLabel?: (value: any, meta?: 'metaLabel' | 'infoSource' | 'infoImage') => string;
    @property({type: Boolean, reflect: true})
    required = false;
    @property({type: Boolean, reflect: true})
    disabled = false;
    @property({type: Function})
    changeApproval?: () => Promise<'continue' | 'cancel'>;
    @property({type: String})
    dialogHeader = '';
    @property({type: String})
    placeholder = '';
    @property({type: Map})
    dialogButtonLabels: SpHierarchicalSelectDialogButtonLabels;
    @property({type: Boolean})
    multi = false;

    @queryAsync('sp-default-dialog') dialog: Promise<SpDefaultDialog>;

    static styles = [
        //language=CSS
        css`
            :host {
                display: block;
                width: auto;
                height: 45px;
            }

            .container {
                display: flex;
                width: 100%;
                height: 100%;
                background-color: #fff;
                box-shadow: var(--sp-default-box-shadow);
                border-radius: 3px;
                cursor: pointer;
                overflow: hidden;
            }

            .label {
                display: flex;
                align-items: center;
                flex: 1 0 0;
                min-width: 0;
                box-sizing: border-box;
                padding: 0 10px;
            }

            .label span {
                display: block;
                width: 100%;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .dots {
                width: auto;
                height: 100%;
                aspect-ratio: 70 / 45;
                background-color: rgb(var(--sp-color-b));
                background-image: url(${unsafeCSSIcon('ellipsis-h-square', {color: 'white', size: 'x128'})});
                background-repeat: no-repeat;
                background-position: center center;
                background-size: auto 55%;
            }

            .reset {
                aspect-ratio: 1 / 1;
                height: 100%;
                background-image: url(${unsafeCSSIcon('close', {color: 'grey-light', size: 'x128'})});
                background-repeat: no-repeat;
                background-position: center center;
                background-size: auto 45%;
                background-color: #fff;
                cursor: pointer;
            }

            .placeholder {
                color: #aaa;
            }

            .active {
                color: #000;
            }

            .disabled {
                pointer-events: none;
                cursor: not-allowed;
                color: #aaa;
            }
        
            .disabled .dots {
                background-color: rgb(var(--sp-color-d)) !important;
            }`
    ];

    willUpdate(props: PropertyValues) {
        super.willUpdate(props);
        if (props.has('options') && this.options && Array.isArray(this.options)) {
            this.options = this.options.map(_option => {
                if (_option.parentId === undefined || _option.parentId === null) _option.parentId = 0;
                return _option;
            });
        }
    }

    async openModal() {
        (await this.dialog).show();
    }

    reset() {
        this.emitChangeEvent(this.multi ? [] : null);
    }

    emitChangeEvent(value: (string | number) | (string | number)[]): void {
        this.value = value;
        this.dispatchEvent(
            new CustomEvent('change',
                {
                    bubbles: true, composed: true, detail: {
                        value
                    }
                }) as SpHierarchicalSelectChangeEvent
        );
    }

    hasNoValue() {
        return (!this.value || this.value === 0 || this.value === '0' || (Array.isArray(this.value) && this.value?.length < 1));
    }

    getLabel(value: string | number | (string|number)[], meta?: 'metaLabel' | 'infoSource' | 'infoImage', hasNoValue = false) {
        if (hasNoValue) return this.placeholder;
        if (this.valueToLabel) return this.valueToLabel(value, meta);
        if (Array.isArray(value)) {
            return value.map(_value => {
                return this.options.find(_option => _option.id === _value).label ?? _value;
            }).join(', ');
        }
        let label = this.options.find(_option => _option.id === value)?.label;
        if (typeof label === 'string')
            label = unsafeHTML(label);
        return label ?? value;
    }

    render() {
        const noValue = this.hasNoValue();
        return html`
            <sp-default-dialog
                    .template=${
                            html`
                                <sp-hierarchical-select-dialog
                                        .dialog=${this.dialog}
                                        .options=${this.options}
                                        .value=${this.value}
                                        .multi=${this.multi}
                                        .required=${this.required}
                                        .disabled=${this.disabled}
                                        .changeApproval=${this.changeApproval}
                                        .getLabel=${this.getLabel.bind(this)}
                                        .emitChange=${this.emitChangeEvent.bind(this)}>
                                </sp-hierarchical-select-dialog>
                            `
                    }
                    .fullHeight="${true}"
                    .headerText="${this.dialogHeader}">
            </sp-default-dialog>
            <div
                    class="${
                            classMap({
                                'container': true,
                                disabled: this.disabled,
                                active: !noValue && !this.disabled,
                                placeholder: noValue && !this.disabled
                            })}">
                <div class="label" @click="${this.openModal}">
                    <span>${this.getLabel(this.value, undefined, noValue)}</span>
                </div>
                ${
                        !this.disabled && !this.required && !noValue ?
                                html`
                                    <div class="reset" @click=${this.reset}></div>` : nothing
                }
                <div class="dots" @click="${this.openModal}"></div>
            </div>
        `;
    }
}

export interface SpHierarchicalSelectChangeEvent extends Event {
    detail: {
        value: (string | number) | (string | number)[];
    }
}

export interface SpHierarchicalSelectOption {
    id: number | string;
    label?: string | TemplateResult | DirectiveResult;
    parentId?: number | string;
    hasMetaLabel?: number;
    hasInfo?: number;
}

export type SpHierarchicalSelectDialogButtonLabels = {
    [key in 'ok' | 'back' | 'cancel']: string | TemplateResult | DirectiveResult;
}
